import React from 'react';
import './RegionShape.css';

// Bins are rectangles by default. A custom-shaped bin has `points`: [[x, y], ...] in percent of
// the photo, while x / y / width / height hold its bounding box. Regions are rendered as a box
// (for labels and badges); custom shapes add this outline inside the box.

export const isShape = (region) => Array.isArray(region?.points) && region.points.length >= 3;

// The bounding box of a set of points, in the same units
export function boundsOf(points) {
  const xs = points.map(p => p[0]);
  const ys = points.map(p => p[1]);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

// The four corners of a rectangle region, clockwise from top-left
export function rectanglePoints({ x, y, width, height }) {
  const [x0, y0, w, h] = [x, y, width, height].map(Number);
  return [[x0, y0], [x0 + w, y0], [x0 + w, y0 + h], [x0, y0 + h]];
}

// Custom shapes put their label at the centre of the outline (the box's bottom edge can be
// outside the shape, e.g. under a triangle). Returns an inline style, or undefined for rectangles.
export function shapeLabelStyle(region) {
  if (!isShape(region)) return undefined;
  const cx = region.points.reduce((sum, p) => sum + Number(p[0]), 0) / region.points.length;
  const cy = region.points.reduce((sum, p) => sum + Number(p[1]), 0) / region.points.length;
  const w = Number(region.width) || 1;
  const h = Number(region.height) || 1;
  return {
    left: `${((cx - Number(region.x)) / w) * 100}%`,
    top: `${((cy - Number(region.y)) / h) * 100}%`,
    bottom: 'auto',
    transform: 'translate(-50%, -50%)',
  };
}

// Bin colours. null is the default highlighter yellow; any other #rrggbb also works.
export const ZONE_COLORS = [
  { value: null, name: 'Yellow' },
  { value: '#ff8a3d', name: 'Orange' },
  { value: '#f0525b', name: 'Red' },
  { value: '#f472b6', name: 'Pink' },
  { value: '#a78bfa', name: 'Purple' },
  { value: '#4f9cf9', name: 'Blue' },
  { value: '#2dd4bf', name: 'Teal' },
  { value: '#4ade80', name: 'Green' },
];

const hexToRgb = (hex) => [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));

// Inline style that repaints a bin in its own colour: it overrides the highlighter tokens the
// region styles are built on. (--hl-soft is set too: on :root it's already computed from yellow.)
export function zoneStyle(color) {
  if (!/^#[0-9a-f]{6}$/i.test(color || '')) return undefined;
  const rgb = hexToRgb(color);
  const strong = `rgb(${rgb.map(c => Math.round(c * 0.8)).join(', ')})`;
  return {
    '--hl': color,
    '--hl-strong': strong,
    '--hl-rgb': rgb.join(', '),
    '--hl-soft': `rgba(${rgb.join(', ')}, 0.38)`,
  };
}

// The outline, drawn in the region box's own 0-100 space so it scales with the photo
export function RegionShape({ region }) {
  if (!isShape(region)) return null;
  const x = Number(region.x);
  const y = Number(region.y);
  const w = Number(region.width) || 1;
  const h = Number(region.height) || 1;
  const local = region.points
    .map(([px, py]) => `${(((px - x) / w) * 100).toFixed(2)},${(((py - y) / h) * 100).toFixed(2)}`)
    .join(' ');

  return (
    <svg className="region-shape" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <polygon points={local} />
    </svg>
  );
}
